import RestoreIcon from "@mui/icons-material/Restore";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import {
  proposalChangeRedlineValues,
  proposalChangeTransitionCarriesItemLabel,
  type ProposalChangeDescriptor,
} from "work/proposals/negotiation/proposal-change-description";
import { groupProposalChangesBySection } from "work/proposals/negotiation/proposal-change-sections";
import ProposalChangeValueRedline from "work/proposals/negotiation/proposal-change-value-redline";
import ProposalFeeScheduleChangeRedline from "work/proposals/negotiation/proposal-fee-schedule-change-redline";
import {
  partitionProposalFeeScheduleChanges,
  summarizeProposalFeeScheduleDescriptors,
} from "work/proposals/negotiation/proposal-fee-schedule-change-summary";

/**
 * The change list in two columns on a wide dialog.
 *
 * A submission's list is as long as the proposal is big, and read as one column it was a wall the
 * reader scrolled past rather than checked (#4538). Two columns halve the height without dropping a
 * row, which is the only outcome that suits BOTH surfaces that still draw a list: a revision of a
 * hired work agreement, where the list is informational, and the resubmission, where each row carries
 * the Revert button that is the whole point of showing it. A negotiation's genuine first submission
 * has nothing published behind it to differ from and passes no changes at all (#4952).
 *
 * `columnCount` rather than a grid: sections are different heights, and a two-column grid would align
 * them in rows and leave a gap beside every short section. Flowing them balances the columns instead.
 * A section never splits across the fold — a heading stranded at the bottom of the left column would
 * file its rows under the wrong subject — so the unit that avoids the break is the whole section.
 *
 * One column below `md`. Two columns of a narrow dialog is worse than one: the fee-schedule redline is
 * a horizontal composition ("Notice of Opposition — Fixed Fee: $4,800 | Currency: USD") that wraps into
 * a paragraph and stops reading as a redline once a column is much under 400px.
 */
const ChangeColumns = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    columnCount: 2,
    columnGap: theme.spacing(4),
  },
}));

const ChangeSection = styled("div")(() => ({
  breakInside: "avoid",
}));

/**
 * A change row is a flex line: the change on the left, its Revert button on the right. Both halves have
 * to be told what to do when the row runs out of room, or a wrapping value slides under the button —
 * which a narrow column makes routine rather than rare.
 */
const changeRowSx = { pl: 2, gap: 2, alignItems: "flex-start" } as const;
const revertButtonSx = { flexShrink: 0 } as const;

/**
 * The one dialog a submission goes through.
 *
 * It used to be two: a plain "Submit the proposal now?" confirm, and then — only sometimes, and only
 * after the save had already run — a second dialog asking which changes to send. Two dialogs for one
 * decision meant the user confirmed before seeing what they were confirming. This one names every
 * change in the same words the field change history uses, says who receives it, and lets the leader
 * explicitly restore an unwanted change before the whole proposal is submitted.
 *
 * The changes are grouped by subject (see `proposal-change-sections.ts`) rather than listed in the order
 * they were made, following the proposal tab order, and no row is ever left with a blank value: a change
 * with nothing on its new side reads "[Empty]".
 *
 * It is about changes and nothing else. It used to also list the author's unpublished comments and ask
 * whether to send them, because a comment was staged until a save or submit delivered it; a comment is
 * published as it is posted now (see `proposal-comment-publishing.ts`), so a submission has no comment
 * decision left to put to the user.
 */

export type SubmitProposalDialogProps = {
  title: string;
  /** Everything this submission would send, already described in the shared change vocabulary. */
  changes: readonly ProposalChangeDescriptor[];
  /** Who receives it — "the vendor", "the client". */
  audienceLabel: string;
  /**
   * Heading over the list: "Proposal summary" for a first-time offer, "Changes to the Work Agreement"
   * for a revision of a hired one, "Changes since your last submission" for a re-submission. See
   * `proposalSubmissionSummaryHeading`.
   */
  changesHeading: string;
  /** Whether the leader can restore values from an earlier submission. */
  allowRevert: boolean;
  /**
   * Warning above the list — that the leader's own reviewers have not finished with what is about to
   * be sent. See `proposal-pending-review-warning.ts`; absent when there is nothing to warn about.
   */
  message?: string;
  confirmButtonText: string;
  onSubmit: (result: SubmitProposalDialogResult) => void;
  onCancel: () => void;
};

export type SubmitProposalDialogResult = {
  revertedChanges: readonly ProposalChangeDescriptor[];
};

export default function SubmitProposalDialog({
  title,
  changes,
  audienceLabel,
  changesHeading,
  allowRevert,
  message,
  confirmButtonText,
  onSubmit,
  onCancel,
}: Readonly<SubmitProposalDialogProps>) {
  const [revertedKeys, setRevertedKeys] = useState<readonly string[]>([]);

  const isReverted = (change: ProposalChangeDescriptor) => revertedKeys.includes(change.key);
  const revertedChanges = changes.filter(isReverted);
  const sections = groupProposalChangesBySection(changes);

  function toggleRevert(change: ProposalChangeDescriptor) {
    setRevertedKeys((current) =>
      current.includes(change.key) ? current.filter((key) => key !== change.key) : [...current, change.key],
    );
  }

  /**
   * Reverts (or keeps) a group of changes as one act.
   *
   * The fee schedule is shown as one thing, so it has to revert as one thing: reverting only the leaf
   * a reader happened to click would leave the schedule half-restored, in a state nobody chose.
   */
  function toggleRevertAll(changes: readonly ProposalChangeDescriptor[], isCurrentlyReverted: boolean) {
    const keys = changes.map((change) => change.key);
    setRevertedKeys((current) =>
      isCurrentlyReverted
        ? current.filter((key) => !keys.includes(key))
        : [...current, ...keys.filter((key) => !current.includes(key))],
    );
  }

  function handleSubmit() {
    onSubmit({ revertedChanges });
  }

  /**
   * Width follows the list, because the list is the only thing that needs the room: two columns of
   * change rows want `md`, and a submission that shows no list at all — a negotiation's genuine first
   * one — is a plain confirmation, which at `md` was a near-empty box the width of the page around one
   * sentence (#5128). `sm` is what every other confirmation in the app is (see `confirm-dialog.tsx`),
   * so the two read as the same kind of question.
   */
  const listIsShown = changes.length > 0;

  return (
    <Dialog
      open
      onClose={onCancel}
      fullWidth
      maxWidth={listIsShown ? "md" : "sm"}
      aria-labelledby="submit-proposal-title"
    >
      <DialogTitle id="submit-proposal-title">{title}</DialogTitle>
      <DialogContent>
        {/* An Alert rather than more prose: this says the submission is going out ahead of someone,
            which is not the same kind of sentence as the one naming who receives it. */}
        {message ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {message}
          </Alert>
        ) : null}
        <DialogContentText>
          {allowRevert
            ? `This sends every listed change to ${audienceLabel}. Revert any change you do not want to submit.`
            : `This sends the proposal to ${audienceLabel}.`}
        </DialogContentText>

        {listIsShown && (
          <>
            <Typography id="submit-proposal-changes-heading" variant="subtitle2" component="h3" sx={{ mt: 2 }}>
              {changesHeading}
            </Typography>
            {/*
              Grouped by subject rather than listed in the order the edits happened, and flowed into
              two columns on a wide dialog so a big submission is a page rather than a scroll.
            */}
            <ChangeColumns>
            {sections.map((section) => {
              // The whole fee schedule as one description of itself, drawn exactly as the change
              // history draws it, instead of one row per semantic leaf. It reverts as one thing too:
              // the reader is being shown the schedule, so that is the unit they can restore.
              const { feeScheduleChanges, otherChanges } = partitionProposalFeeScheduleChanges(section.changes);
              const feeScheduleIsReverted =
                feeScheduleChanges.length > 0 && feeScheduleChanges.every(isReverted);
              return (
              <ChangeSection key={section.title}>
                <Typography
                  variant="overline"
                  component="h4"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1.5 }}
                >
                  {section.title}
                </Typography>
                <List dense disablePadding aria-label={section.title}>
                  {feeScheduleChanges.length > 0 && (
                    <ListItem key="fee-schedule" sx={{ ...changeRowSx, opacity: feeScheduleIsReverted ? 0.65 : 1 }}>
                      <ListItemText
                        primary="Fee Schedule"
                        secondary={
                          feeScheduleIsReverted ? (
                            "Will be restored before submission"
                          ) : (
                            <ProposalFeeScheduleChangeRedline
                              summary={summarizeProposalFeeScheduleDescriptors(feeScheduleChanges)}
                            />
                          )
                        }
                        slotProps={{ secondary: { component: "span" } }}
                      />
                      {allowRevert ? (
                        <Button
                          size="small"
                          sx={revertButtonSx}
                          color={feeScheduleIsReverted ? "inherit" : "secondary"}
                          startIcon={<RestoreIcon />}
                          onClick={() => toggleRevertAll(feeScheduleChanges, feeScheduleIsReverted)}
                          aria-label={`${feeScheduleIsReverted ? "Keep" : "Revert"} Fee Schedule`}
                        >
                          {feeScheduleIsReverted ? "Keep change" : "Revert"}
                        </Button>
                      ) : null}
                    </ListItem>
                  )}
                  {otherChanges.map((change) => {
                    const label = changeLabel(change);
                    // Never a blank value: a change with nothing on its new side reads "[Empty]", so an
                    // empty field is distinguishable from a value the dialog could not render.
                    const { previousValue, nextValue } = proposalChangeRedlineValues(change);
                    // ...except when the value would only repeat the name the subject line already gave —
                    // an added document or member, whose value IS its name. Same rule the save
                    // confirmation's list applies, so the two cannot disagree about the duplicate.
                    const showsRedline =
                      previousValue !== undefined || !proposalChangeTransitionCarriesItemLabel(change);
                    const detail = showsRedline ? (
                      <ProposalChangeValueRedline previousValue={previousValue} nextValue={nextValue} />
                    ) : undefined;
                    return (
                      <ListItem key={change.key} sx={{ ...changeRowSx, opacity: isReverted(change) ? 0.65 : 1 }}>
                        <ListItemText
                          primary={label}
                          secondary={isReverted(change) ? "Will be restored before submission" : detail}
                          slotProps={{ secondary: { component: "span" } }}
                        />
                        {allowRevert ? (
                          <Button
                            size="small"
                            sx={revertButtonSx}
                            color={isReverted(change) ? "inherit" : "secondary"}
                            startIcon={<RestoreIcon />}
                            onClick={() => toggleRevert(change)}
                            aria-label={`${isReverted(change) ? "Keep" : "Revert"} ${label}`}
                          >
                            {isReverted(change) ? "Keep change" : "Revert"}
                          </Button>
                        ) : null}
                      </ListItem>
                    );
                  })}
                </List>
              </ChangeSection>
              );
            })}
            </ChangeColumns>
          </>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** "Fee Schedule — Essential fixed fee" reads better in a list than the full one-line sentence. */
function changeLabel(change: ProposalChangeDescriptor): string {
  return change.itemLabel ? `${change.fieldLabel} — ${change.itemLabel}` : change.fieldLabel;
}
